import React from 'react';
import { XIcon, PackageIcon, CalendarIcon, CreditCardIcon, TruckIcon } from 'lucide-react';
interface ReturnItem {
  name: string;
  quantity: number;
  price: number;
  image: string;
}
interface ReturnData {
  id: string;
  originalOrderId: string;
  returnDate: string;
  status: 'approved' | 'pending' | 'rejected';
  reason: string;
  refundMethod: string;
  refundAmount: number;
  items: ReturnItem[];
  trackingNumber?: string;
  estimatedRefundDate?: string;
}
interface ReturnDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  returnData: ReturnData;
}
const STATUS_STYLES: Record<string, string> = {
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-yellow-100 text-yellow-700',
  rejected: 'bg-red-100 text-red-700'
};
export function ReturnDetailsModal({
  isOpen,
  onClose,
  returnData
}: ReturnDetailsModalProps) {
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-id="element-1111" data-mp="qptiyboft3wl">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl" data-id="element-1112" data-mp="17chgo5knedjg">
        <div className="sticky top-0 bg-[#1E2B55] text-white flex items-center justify-between p-5 z-10" data-id="element-1113" data-mp="13bxiqzs935w9">
          <div data-id="element-1114" data-mp="yahz9c5q17fz">
            <h2 className="text-lg font-bold" data-id="element-1115" data-mp="ye5s093cdptn">{returnData.id}</h2>
            <p className="text-sm text-blue-200" data-id="element-1116" data-mp="tcq6kfx95haq">
              Original Order: {returnData.originalOrderId}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10" aria-label="Close" data-id="element-1117" data-mp="jngev16bm0n5">
            <XIcon className="w-5 h-5" data-id="element-1118" data-mp="fmmq65l20ht7" />
          </button>
        </div>

        <div className="p-5 space-y-5" data-id="element-1119" data-mp="ana2xjq9t8q3">
          <div className="flex items-center justify-between" data-id="element-1120" data-mp="aobdzo7adv7t">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_STYLES[returnData.status] || 'bg-gray-100 text-gray-700'}`} data-id="element-1121" data-mp="5nx6c79lknab">
              {returnData.status.charAt(0).toUpperCase() + returnData.status.slice(1)}
            </span>
            <div className="text-right" data-id="element-1122" data-mp="3s68olr0imp9x">
              <p className="text-xs text-gray-500" data-id="element-1123" data-mp="3s32zwcvstpeq">Refund Amount</p>
              <p className="text-xl font-bold text-[#1E2B55]" data-id="element-1124" data-mp="3e790qlktueet">
                SAR {returnData.refundAmount.toFixed(2)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-1125" data-mp="9r6ggpyskb9o">
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg" data-id="element-1126" data-mp="eqjca07n4soe">
              <CalendarIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-1127" data-mp="esndgae5ndkz" />
              <div data-id="element-1128" data-mp="ycuyasr4fnd">
                <p className="text-xs text-gray-500" data-id="element-1129" data-mp="x1uokyilq10">Return Date</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-1130" data-mp="4zohg7gbwkrc">
                  {new Date(returnData.returnDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg" data-id="element-1131" data-mp="2i5cyq1h9dceq">
              <CreditCardIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-1132" data-mp="2d7tjozcqkvlq" />
              <div data-id="element-1133" data-mp="2rxirvej8xzvi">
                <p className="text-xs text-gray-500" data-id="element-1134" data-mp="2rwpqkt3242kr">Refund Method</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-1135" data-mp="2mxwb4v47m1hy">
                  {returnData.refundMethod}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg md:col-span-2" data-id="element-1136" data-mp="1zaeofdzsxtlu">
              <PackageIcon className="w-4 h-4 text-gray-500 mt-0.5" data-id="element-1137" data-mp="1ubv2vep4eju0" />
              <div data-id="element-1138" data-mp="298cd22lkpsez">
                <p className="text-xs text-gray-500" data-id="element-1139" data-mp="294okb5nyda1b">Reason</p>
                <p className="text-sm font-medium text-gray-900" data-id="element-1140" data-mp="246veweuh4k23">
                  {returnData.reason}
                </p>
              </div>
            </div>
            {returnData.trackingNumber && <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg md:col-span-2" data-id="element-1141" data-mp="3fm1xfmawvyjw">
                <TruckIcon className="w-4 h-4 text-blue-600 mt-0.5" data-id="element-1142" data-mp="1p71aowlt7hjl" />
                <div data-id="element-1143" data-mp="1u4ayy8xgpn5u">
                  <p className="text-xs text-gray-500" data-id="element-1144" data-mp="1u5uhk66lcaas">Tracking Number</p>
                  <p className="text-sm font-medium text-blue-700" data-id="element-1145" data-mp="1y5wgw8ousrmw">
                    {returnData.trackingNumber}
                  </p>
                </div>
              </div>}
            {returnData.estimatedRefundDate && <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg md:col-span-2" data-id="element-1146" data-mp="2842h8lv0hppn">
                <CalendarIcon className="w-4 h-4 text-green-600 mt-0.5" data-id="element-1147" data-mp="2d242ndfwu14a" />
                <div data-id="element-1148" data-mp="2hvgcuijue5di">
                  <p className="text-xs text-gray-500" data-id="element-1149" data-mp="2hux4ntjzuvco">Estimated Refund Date</p>
                  <p className="text-sm font-medium text-green-700" data-id="element-1150" data-mp="2luxknimqrelu">
                    {new Date(returnData.estimatedRefundDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </p>
                </div>
              </div>}
          </div>

          <div data-id="element-1151" data-mp="3113bxsrcvbe9">
            <h3 className="text-sm font-bold text-gray-900 mb-3" data-id="element-1152" data-mp="311czl9oxh6gs">
              Returned Items
            </h3>
            <div className="space-y-3" data-id="element-1153" data-mp="2rylnd7o8a6o3">
              {returnData.items.map((item, idx) => <div key={idx} className="flex gap-3 p-3 border border-gray-200 rounded-lg" data-id="element-1154" data-mp="3ool4hqig2dia" data-mpi={idx}>
                  <img src={item.image} alt={item.name} className="w-16 h-16 rounded-lg object-cover" data-id="element-1155" data-mp="3jqt3m6ztnh1y" />
                  <div className="flex-1" data-id="element-1156" data-mp="3aw50amdt49fs">
                    <p className="font-medium text-gray-900 line-clamp-2" data-id="element-1157" data-mp="3av3nacd8dcvf">
                      {item.name}
                    </p>
                    <p className="text-sm text-gray-600" data-id="element-1158" data-mp="6j83y2onz24s">
                      Qty: {item.quantity}
                    </p>
                  </div>
                  <p className="font-bold text-gray-900" data-id="element-1159" data-mp="20rbxwu2h939o">
                    SAR {item.price.toFixed(2)}
                  </p>
                </div>)}
            </div>
          </div>

          <button onClick={onClose} className="w-full px-4 py-2.5 bg-[#1E2B55] text-white font-medium rounded-lg hover:bg-[#162041]" data-id="element-1160" data-mp="1r0hc1qt585or">
            Close
          </button>
        </div>
      </div>
    </div>;
}